/**
 * Anthra AI — Analytics client
 *
 * The provider-agnostic core. Owns identity, batching, consent, standard
 * attributes and the PII guard; delegates delivery to one or more adapters.
 *
 * WHAT THIS BUYS YOU
 *   - Swap or add a provider by changing one line in providers/index.ts. No call
 *     site changes, ever.
 *   - Run two providers during a migration, so the new one can be validated
 *     against the old before cutting over.
 *   - The PII guard runs once, centrally. A single `track('signup', { email })`
 *     cannot leak, because the guard strips it before any adapter sees it
 *     (ANALYTICS.md §8).
 *
 * USAGE — always through the typed helper in ./events, never this directly:
 *   import { track } from '../analytics';
 *   track('query_submitted', { category, attachment_count });
 */

import { host } from "../host"
import { logger } from "../logger"
import type {
	AnalyticsConfig,
	AnalyticsEvent,
	AnalyticsProvider,
	EventAttributes,
	SessionAttributes,
	StandardAttributes,
	UserAttributes,
} from "./types"

// -----------------------------------------------------------------------------
// PII guard
//
// The last line of defence before an attribute reaches a third party. Once an
// event is in a provider's warehouse it is very hard to un-send — which is why
// this strips rather than warns (ANALYTICS.md §8).
// -----------------------------------------------------------------------------

const FORBIDDEN_KEYS = new Set([
	"email",
	"email_address",
	"name",
	"first_name",
	"last_name",
	"full_name",
	"phone",
	"phone_number",
	"mobile",
	"address",
	"street",
	"postcode",
	"zip",
	"dob",
	"date_of_birth",
	"ssn",
	"aadhaar",
	"pan",
	"passport",
	"card_number",
	"cvv",
	"ip",
	"ip_address",
	"latitude",
	"longitude",
	"password",
	"token",
	"search_query",
	"query",
	"free_text",
	"comment",
	"note",
	// ---- Swamithra's own spellings (event-design-sheet.yml, `gaps`) -----------
	// The typed registry declares none of these, so nothing emits them today —
	// this is the second control, widened to match the first rather than left to
	// catch up with it the day a call site adds one by hand.
	"mobile_number",
	"title",
	"description",
	"notes",
	"file_name",
	"filename",
	"search",
	"body",
	"body_rich",
	"response_text",
	"review_body_rich",
])

const EMAIL_RE = /[\w.+-]+@[\w-]+\.[\w.]+/

function stripPII(name: string, attributes: EventAttributes): EventAttributes {
	const clean: EventAttributes = {}

	for (const [key, value] of Object.entries(attributes)) {
		const k = key.toLowerCase()

		if (FORBIDDEN_KEYS.has(k)) {
			logger.warn("analytics: PII attribute stripped", { event: name, attribute: key })
			continue
		}
		if (typeof value === "string" && EMAIL_RE.test(value)) {
			logger.warn("analytics: value looked like an email and was stripped", {
				event: name,
				attribute: key,
			})
			continue
		}
		// A very long string is almost always free text a user typed.
		if (typeof value === "string" && value.length > 200) {
			logger.warn("analytics: attribute truncated", { event: name, attribute: key })
			clean[key] = value.slice(0, 200)
			continue
		}
		clean[key] = value
	}

	return clean
}

// -----------------------------------------------------------------------------
// Client
// -----------------------------------------------------------------------------

export class AnalyticsClient {
	private providers: AnalyticsProvider[] = []
	private queue: AnalyticsEvent[] = []
	private timer: ReturnType<typeof setInterval> | undefined
	private initialised = false
	private initialising: Promise<void> | undefined
	private consentGranted = false

	private anonymousId: string
	private sessionId: string
	private userId: string | undefined
	private tenantId: string | undefined
	private screenName = "unknown"
	private correlationId: string | undefined
	private sessionAttributes: Partial<SessionAttributes> = {}

	constructor(private readonly config: AnalyticsConfig) {
		// Provisional. The PERSISTED id is read in init(), because storage is async
		// on native (SecureStore/AsyncStorage) and a constructor cannot await.
		//
		// This used to read localStorage synchronously and fall back to a fresh id
		// when it was absent. On React Native localStorage is always absent, so
		// every launch minted a new anonymous id and EVERY RETURNING USER LOOKED
		// NEW. Nothing errors — retention, funnels and DAU are simply wrong, in the
		// direction that flatters them.
		this.anonymousId = createId()
		this.sessionId = createId()
	}

	/** Registers adapters. Call before init. */
	register(...providers: AnalyticsProvider[]): this {
		this.providers.push(...providers)
		providers.forEach(provider => void provider.setConsent?.(this.consentGranted))
		return this
	}

	async init(): Promise<void> {
		if (this.initialised || !this.config.enabled) return
		if (this.initialising) return this.initialising

		const initialise = this.initialise()
		this.initialising = initialise
		try {
			await initialise
		} finally {
			if (this.initialising === initialise) this.initialising = undefined
		}
	}

	private async initialise(): Promise<void> {
		// Adopt the persisted anonymous id, if there is one.
		//
		// Queued events do not need re-stamping: the anonymous id is not an event
		// attribute, it is only used by alias() to link the pre-login session to the
		// account. identify() happens long after init(), so it always sees the
		// adopted id.
		const stored = await host().storage.get(ANON_KEY)
		if (stored) {
			this.anonymousId = stored
		} else {
			await host().storage.set(ANON_KEY, this.anonymousId)
		}

		if (this.config.requireConsent && !this.consentGranted) return
		this.initialised = true

		await Promise.all(
			this.providers.map(async p => {
				try {
					await p.init()
				} catch (error) {
					// A provider that fails to load must never break the application. The
					// product working matters more than the measurement working.
					logger.warn(
						"analytics provider failed to initialise",
						{ provider: p.name },
						error,
					)
				}
			}),
		)

		this.timer = setInterval(() => this.flush(), this.config.flushIntervalMs)

		// Events at the END of a funnel are the ones lost when the app goes away,
		// and they are the most valuable ones.
		host().onBackground(() => this.flush())
	}

	/** Records the consent decision. No event is emitted until this is true. */
	setConsent(granted: boolean): void {
		this.consentGranted = granted
		this.providers.forEach(provider => void provider.setConsent?.(granted))
		if (!granted) {
			this.queue = []
			this.providers.forEach(p => p.reset())
			return
		}
		if (!this.initialised) {
			void this.init().catch((error: unknown) => {
				logger.warn("analytics failed to initialise", {}, error)
			})
		}
	}

	setScreen(name: string): void {
		this.screenName = name
		if (!this.canEmit()) return
		this.providers.forEach(p => p.screen?.(name))
	}

	/** Bound from the last API response, joining events to server traces. */
	setCorrelationId(id: string | undefined): void {
		this.correlationId = id
	}

	setSessionAttributes(attributes: Partial<SessionAttributes>): void {
		this.sessionAttributes = { ...this.sessionAttributes, ...attributes }
	}

	/**
	 * Call on login. Aliases the anonymous ID to the user, then identifies.
	 *
	 * Order matters: alias BEFORE identify, or the provider has already started a
	 * new identity graph and the pre-login events are orphaned.
	 */
	identify(attributes: UserAttributes): void {
		if (!this.canEmit()) return

		if (EMAIL_RE.test(attributes.user_id)) {
			logger.error("analytics: user_id looks like an email — must be an opaque internal ID", {
				provider: "core",
			})
			return
		}

		this.userId = attributes.user_id
		this.tenantId = attributes.organisation

		this.providers.forEach(p => {
			try {
				p.alias(this.anonymousId, attributes.user_id)
				p.identify(attributes)
			} catch (error) {
				logger.warn("analytics identify failed", { provider: p.name }, error)
			}
		})
	}

	/** Call on logout, or one account appears to be used by five people. */
	reset(): void {
		this.flush()
		this.userId = undefined
		this.tenantId = undefined
		this.anonymousId = createId()
		this.sessionId = createId()
		persistAnonymousId(this.anonymousId)
		this.providers.forEach(p => {
			try {
				p.reset()
			} catch {
				/* a failed reset must not block logout */
			}
		})
	}

	/**
	 * Emit an event. Prefer the typed `track` from ./events — it validates the
	 * attributes against the Event Design Sheet at compile time.
	 */
	track(name: string, attributes: EventAttributes = {}): void {
		if (!this.canEmit()) return

		const event: AnalyticsEvent = {
			name,
			attributes: {
				...stripPII(name, attributes),
				...this.standardAttributes(),
			},
		}

		if (this.config.debug) {
			logger.debug("analytics event (debug mode — not sent)", {
				event: name,
				attributes: event.attributes,
			})
			return
		}

		this.queue.push(event)
		logger.debug("analytics: event added to delivery queue", {
			event: name,
			queue_size: this.queue.length,
		})
		// GA4 already performs its own transport scheduling. Hand browser events to
		// gtag immediately so successful funnel actions are observable in DebugView
		// and are not stranded when the next screen unmounts their caller.
		if (this.providers.some(provider => provider.name === "ga4")) {
			this.flush()
		} else if (this.queue.length >= this.config.batchSize) {
			this.flush()
		}
	}

	flush(): void {
		if (!this.canEmit()) {
			this.queue = []
			return
		}
		if (this.queue.length === 0) return
		const batch = this.queue
		this.queue = []

		for (const provider of this.providers) {
			for (const event of batch) {
				try {
					logger.debug("analytics: dispatching event to provider", {
						event: event.name,
						provider: provider.name,
					})
					provider.track(event)
				} catch (error) {
					logger.warn(
						"analytics track failed",
						{ provider: provider.name, event: event.name },
						error,
					)
				}
			}
			void provider.flush?.()
		}
	}

	dispose(): void {
		if (this.timer) clearInterval(this.timer)
		this.flush()
	}

	private standardAttributes(): StandardAttributes & Partial<SessionAttributes> {
		return {
			event_id: createId(),
			timestamp: new Date().toISOString(),
			screen_name: this.screenName,
			platform: this.config.platform,
			app_version: this.config.appVersion,
			session_id: this.sessionId,
			...(this.correlationId ? { correlation_id: this.correlationId } : {}),
			...(this.userId ? { user_id: this.userId } : {}),
			...(this.tenantId ? { tenant_id: this.tenantId } : {}),
			...this.sessionAttributes,
		}
	}

	private canEmit(): boolean {
		if (!this.config.enabled) return false
		if (this.config.requireConsent && !this.consentGranted) return false
		// Respect Do Not Track. Cheap to honour, and ignoring it is indefensible.
		// Browser-only signal; native has no equivalent, and the consent flag above
		// is what carries the same intent there.
		const nav = (globalThis as { navigator?: { doNotTrack?: string } }).navigator
		if (host().name === "web" && nav?.doNotTrack === "1") return false
		return true
	}
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

const ANON_KEY = "anthra.anonymous_id"

function createId(): string {
	if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID()
	return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

function persistAnonymousId(id: string): void {
	// Fire and forget. A failure to persist means this session does not stitch to
	// the next one; it must never break the flow that emitted the event.
	void host().storage.set(ANON_KEY, id)
}
